/**
 * Product activity page — `/products/:sourceProductId` (WSRADM-0045).
 *
 * Header from `GET /admin/products/{id}`; tabs Sales lines / Check-ins.
 * Negative `sourceProductId` values are real legacy-import ids — request and
 * render them; never treat a negative as invalid. Never sum `lineAmount`.
 * Attendance matching is the server's answer — never de-duplicate client-side.
 */
import { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Link,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '@/api/reports/format';
import {
  getProduct,
  listAttendance,
  listProductSales,
} from '@/api/warehouse/warehouseApi';
import type {
  NameMatch,
  ProductSaleLine,
  WarehouseAttendance,
  WarehouseProduct,
} from '@/api/warehouse/types';
import { ErrorDetails } from '@/components/ErrorDetails';
import { RosterTable, type RosterColumn } from '@/components/RosterTable';
import {
  EM_DASH,
  boolLabel,
  formatWarehouseDateTime,
  nullableBoolLabel,
  textOrDash,
} from '@/components/rosterFormat';
import { useCopy } from '@/hooks/useCopy';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useResource } from '@/hooks/useResource';

const PAGE_SIZE_DEFAULT = 20;

type ProductTab = 'sales' | 'checkins';

function parseTab(raw: string | null): ProductTab {
  return raw === 'checkins' ? 'checkins' : 'sales';
}

function parseNonNegInt(raw: string | null, fallback: number): number {
  if (raw === null || raw === '') return fallback;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return fallback;
  return Math.floor(n);
}

/** Integer path segment — allows negatives (legacy synthetics); rejects `4711abc`. */
function parseSourceProductId(raw: string | undefined): number | null {
  if (raw === undefined || raw === '') return null;
  if (!/^-?\d+$/.test(raw)) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return n;
}

function tabDefaults(tab: ProductTab): { sort: string; order: 'asc' | 'desc' } {
  return tab === 'checkins'
    ? { sort: 'attendedOn', order: 'desc' }
    : { sort: 'saleDate', order: 'desc' };
}

function HeaderRow({ label, value }: { label: string; value: string }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
      <Box component="span" sx={{ color: 'text.primary', mr: 1 }}>
        {label}:
      </Box>
      {value}
    </Typography>
  );
}

function matchTitle(
  match: NameMatch,
  t: (key: string, fallback?: string) => string,
): string | undefined {
  if (match === 'AMBIGUOUS') {
    return t(
      'warehouse.attendance.matchAmbiguous',
      "Several members share this name, so this row can't be linked to one.",
    );
  }
  if (match === 'UNMATCHED') {
    return t('warehouse.attendance.matchUnmatched', 'No member matches this name.');
  }
  return undefined;
}

function MemberNameCell({
  row,
  t,
}: {
  row: WarehouseAttendance;
  t: (key: string, fallback?: string) => string;
}) {
  const label = textOrDash(row.memberName);
  if (row.memberMatch === 'MATCHED' && row.sourceClientId !== null) {
    return (
      <Link component={RouterLink} to={`/users/${row.sourceClientId}`}>
        {label}
      </Link>
    );
  }
  const title = matchTitle(row.memberMatch, t);
  if (title !== undefined) {
    return <span title={title}>{label}</span>;
  }
  return label;
}

function ProductHeader({
  product,
  yes,
  no,
  t,
  locale,
}: {
  product: WarehouseProduct;
  yes: string;
  no: string;
  t: (key: string, fallback?: string) => string;
  locale: string;
}) {
  const title =
    product.name !== null && product.name.length > 0
      ? product.name
      : `#${product.sourceProductId}`;
  const priceLabel =
    product.price === null
      ? EM_DASH
      : formatMoney(product.price, product.currency ?? '', locale);
  const pendingLabel =
    product.pendingVisits === null && product.hasPendingVisits === null
      ? EM_DASH
      : [
          product.pendingVisits !== null ? String(product.pendingVisits) : null,
          product.hasPendingVisits !== null
            ? nullableBoolLabel(product.hasPendingVisits, yes, no)
            : null,
        ]
          .filter((part): part is string => part !== null)
          .join(' · ');

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      <Typography variant="h1" component="h1" sx={{ mb: 2 }}>
        {title}{' '}
        <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.6em' }}>
          #{product.sourceProductId}
        </Box>
      </Typography>
      <HeaderRow
        label={t('warehouse.product.type', 'Type')}
        value={textOrDash(product.productType)}
      />
      <HeaderRow label={t('warehouse.product.price', 'Price')} value={priceLabel} />
      <HeaderRow
        label={t('warehouse.product.duration', 'Duration (days)')}
        value={textOrDash(product.durationDays)}
      />
      <HeaderRow
        label={t('warehouse.product.pendingVisits', 'Pending visits')}
        value={pendingLabel}
      />
      <HeaderRow
        label={t('warehouse.product.group', 'Group')}
        value={nullableBoolLabel(product.isGroup, yes, no)}
      />
      <HeaderRow
        label={t('warehouse.product.created', 'Created')}
        value={textOrDash(product.sourceCreatedAt)}
      />
    </Paper>
  );
}

export function ProductPage() {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const { sourceProductId: rawId } = useParams<{ sourceProductId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const id = parseSourceProductId(rawId);

  const tab = parseTab(searchParams.get('tab'));
  const defaults = tabDefaults(tab);
  const page = parseNonNegInt(searchParams.get('page'), 0);
  const size = parseNonNegInt(searchParams.get('size'), PAGE_SIZE_DEFAULT);
  const sort = searchParams.get('sort') ?? defaults.sort;
  const orderRaw = searchParams.get('order');
  const order: 'asc' | 'desc' =
    orderRaw === 'asc' || orderRaw === 'desc' ? orderRaw : defaults.order;

  const yes = t('common.yes', 'Yes');
  const no = t('common.no', 'No');

  const patchParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next);
  };

  const setTab = (next: ProductTab) => {
    patchParams({
      tab: next === 'sales' ? null : next,
      page: null,
      sort: null,
      order: null,
    });
  };

  const productRes = useResource(() => {
    if (id === null) return Promise.reject(new Error('invalid-id'));
    return getProduct(id);
  }, [id]);

  const fallbackTitle = t('warehouse.product.title', 'Product');
  const docTitle =
    productRes.status === 'ready' && productRes.data !== null
      ? productRes.data.name !== null && productRes.data.name.length > 0
        ? productRes.data.name
        : `#${productRes.data.sourceProductId}`
      : fallbackTitle;
  useDocumentTitle(docTitle);

  const salesRes = useResource(() => {
    if (id === null || tab !== 'sales') {
      return Promise.resolve({
        content: [] as ProductSaleLine[],
        page: 0,
        size: 0,
        totalElements: 0,
        totalPages: 0,
      });
    }
    return listProductSales({
      sourceProductId: id,
      page,
      size,
      sort,
      order,
      search: '',
    });
  }, [id, tab, page, size, sort, order]);

  const checkinsRes = useResource(() => {
    if (id === null || tab !== 'checkins') {
      return Promise.resolve({
        content: [] as WarehouseAttendance[],
        page: 0,
        size: 0,
        totalElements: 0,
        totalPages: 0,
        memberFilterMatch: null,
        memberFilterSharedWith: null,
        productFilterMatch: null,
        productFilterSharedWith: null,
      });
    }
    return listAttendance({
      productId: id,
      page,
      size,
      sort,
      order,
      search: '',
    });
  }, [id, tab, page, size, sort, order]);

  const salesColumns = useMemo<RosterColumn<ProductSaleLine>[]>(
    () => [
      {
        id: 'saleDate',
        header: t('sales.columns.saleDate', 'Sale date'),
        sortId: 'saleDate',
        defaultOrder: 'desc',
        render: (row) => formatWarehouseDateTime(row.saleDate, i18n.language),
      },
      {
        id: 'documentNumber',
        header: t('sales.columns.documentNumber', 'Document'),
        sortId: 'documentNumber',
        render: (row) => textOrDash(row.documentNumber),
      },
      {
        id: 'memberName',
        header: t('sales.columns.member', 'Member'),
        render: (row) => {
          const label = textOrDash(row.memberName);
          if (row.sourceClientId !== null) {
            return (
              <Link component={RouterLink} to={`/users/${row.sourceClientId}`}>
                {label}
              </Link>
            );
          }
          return label;
        },
      },
      {
        id: 'quantity',
        header: t('warehouse.product.quantity', 'Quantity'),
        render: (row) => textOrDash(row.quantity),
      },
      {
        id: 'payedPrice',
        header: t('warehouse.product.unitPrice', 'Unit price'),
        render: (row) =>
          row.payedPrice === null
            ? EM_DASH
            : formatMoney(row.payedPrice, row.currency ?? '', i18n.language),
      },
      {
        id: 'lineAmount',
        header: t('warehouse.product.lineAmount', 'Line amount'),
        render: (row) =>
          row.lineAmount === null
            ? EM_DASH
            : formatMoney(row.lineAmount, row.currency ?? '', i18n.language),
      },
      {
        id: 'paymentAccount',
        header: t('sales.columns.paymentAccount', 'Payment account'),
        render: (row) => textOrDash(row.paymentAccountName),
      },
      {
        id: 'deleted',
        header: t('warehouse.product.voided', 'Voided'),
        render: (row) => boolLabel(row.deleted, yes, no),
      },
    ],
    [i18n.language, no, t, yes],
  );

  const checkinsColumns = useMemo<RosterColumn<WarehouseAttendance>[]>(
    () => [
      {
        id: 'attendedOn',
        header: t('attendance.columns.attendedOn', 'Date'),
        sortId: 'attendedOn',
        defaultOrder: 'desc',
        render: (row) => row.attendedOn,
      },
      {
        id: 'attendedAtTime',
        header: t('attendance.columns.attendedAtTime', 'Time'),
        render: (row) => textOrDash(row.attendedAtTime),
      },
      {
        id: 'className',
        header: t('attendance.columns.className', 'Class'),
        sortId: 'className',
        render: (row) => textOrDash(row.className),
      },
      {
        id: 'memberName',
        header: t('attendance.columns.memberName', 'Member'),
        sortId: 'memberName',
        render: (row) => <MemberNameCell row={row} t={t} />,
      },
      {
        id: 'instructor',
        header: t('attendance.columns.instructor', 'Instructor'),
        sortId: 'instructor',
        render: (row) => textOrDash(row.instructor),
      },
    ],
    [t],
  );

  if (id === null) {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('warehouse.product.notFound', 'That product was not found.')}
        </Alert>
        <Button component={RouterLink} to="/products" variant="text">
          {t('warehouse.product.back', 'Back to products')}
        </Button>
      </Box>
    );
  }

  const sharedWith = checkinsRes.data?.productFilterSharedWith;
  const showSharedCaption =
    tab === 'checkins' &&
    checkinsRes.data?.productFilterMatch === 'AMBIGUOUS' &&
    sharedWith !== null &&
    sharedWith !== undefined;

  return (
    <Box>
      <Button component={RouterLink} to="/products" variant="text" sx={{ mb: 1 }}>
        {t('warehouse.product.back', 'Back to products')}
      </Button>

      {productRes.status === 'loading' ? (
        <Skeleton variant="rounded" height={200} role="status" sx={{ mb: 3 }} />
      ) : null}
      {productRes.status === 'error' ? (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={productRes.refetch}>
              {t('common.retry', 'Retry')}
            </Button>
          }
        >
          {t('roster.loadError', 'Couldn’t load this list.')}
          <ErrorDetails
            intent="read"
            error={productRes.error}
            headline={t('roster.loadError', 'Couldn’t load this list.')}
          />
        </Alert>
      ) : null}
      {productRes.data !== null ? (
        <ProductHeader
          product={productRes.data}
          yes={yes}
          no={no}
          t={t}
          locale={i18n.language}
        />
      ) : null}

      <Tabs
        value={tab}
        onChange={(_event, value: ProductTab) => setTab(value)}
        sx={{ mb: 3 }}
      >
        <Tab value="sales" label={t('warehouse.product.tabSales', 'Sales transactions')} />
        <Tab value="checkins" label={t('warehouse.product.tabCheckins', 'Check-ins')} />
      </Tabs>

      {tab === 'sales' ? (
        <RosterTable
          title={t('warehouse.product.tabSales', 'Sales transactions')}
          columns={salesColumns}
          rows={salesRes.data?.content ?? []}
          rowId={(row) => `${row.sourceSaleId}-${row.sourceDetailId}`}
          status={salesRes.status}
          error={salesRes.error}
          totalElements={salesRes.data?.totalElements ?? 0}
          page={page}
          size={size}
          sort={sort}
          order={order}
          onPageChange={(next) => patchParams({ page: next === 0 ? null : String(next) })}
          onRowsPerPageChange={(next) =>
            patchParams({
              size: next === PAGE_SIZE_DEFAULT ? null : String(next),
              page: null,
            })
          }
          onSortChange={(idKey, nextOrder) => {
            patchParams({
              sort: idKey,
              order: nextOrder,
              page: null,
            });
          }}
          onApply={() => {
            /* product tabs keep search empty */
          }}
          onRetry={salesRes.refetch}
        />
      ) : (
        <>
          {showSharedCaption ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t(
                'warehouse.product.sharedName',
                '{{n}} products share this name; these check-ins may include theirs.',
                { n: sharedWith },
              )}
            </Alert>
          ) : null}
          <RosterTable
            title={t('warehouse.product.tabCheckins', 'Check-ins')}
            columns={checkinsColumns}
            rows={checkinsRes.data?.content ?? []}
            rowId={(row) => row.naturalKeyHash}
            status={checkinsRes.status}
            error={checkinsRes.error}
            totalElements={checkinsRes.data?.totalElements ?? 0}
            page={page}
            size={size}
            sort={sort}
            order={order}
            onPageChange={(next) => patchParams({ page: next === 0 ? null : String(next) })}
            onRowsPerPageChange={(next) =>
              patchParams({
                size: next === PAGE_SIZE_DEFAULT ? null : String(next),
                page: null,
              })
            }
            onSortChange={(idKey, nextOrder) => {
              patchParams({
                sort: idKey,
                order: nextOrder,
                page: null,
              });
            }}
            onApply={() => {
              /* product tabs keep search empty */
            }}
            onRetry={checkinsRes.refetch}
          />
        </>
      )}
    </Box>
  );
}
